package com.trevorwiebe.timesheet.signin.presentation.auth

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.runtime.collectAsState
import com.trevorwiebe.timesheet.core.presentation.common.DialogButtonRow
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveDefaults
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.viewmodel.koinViewModel
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.app_logo

@Composable
fun SignInScreen(
    viewModel: SignInViewModel = koinViewModel(),
    onSignInSuccessful: () -> Unit
) {

    val state by viewModel.state.collectAsState()
    val imePadding = WindowInsets.ime.asPaddingValues()
    val scrollState = rememberScrollState()
    var currentEmail by remember { mutableStateOf("") }

    LaunchedEffect(viewModel.onSignInSuccessful){
        viewModel.onSignInSuccessful.collect{
            // navigate to home screen
            onSignInSuccessful()
        }
    }

    Box(
        modifier = Modifier
            .background(MaterialTheme.colorScheme.background)
            .fillMaxSize()
    ) {
        ResponsiveContent(
            maxWidth = ResponsiveDefaults.formMaxWidth,
            capMinWindowWidth = 0.dp,
        ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp)
                .padding(bottom = imePadding.calculateBottomPadding())
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Spacer(modifier = Modifier.height(64.dp))

            Image(
                painter = painterResource(Res.drawable.app_logo),
                contentDescription = "Santa Fe Carriers logo",
                modifier = Modifier
                    .size(96.dp)
                    .clip(RoundedCornerShape(24.dp)),
            )

            Spacer(modifier = Modifier.height(16.dp))

            Text(
                modifier = Modifier.fillMaxWidth(),
                text = "Santa Fe Carriers",
                textAlign = TextAlign.Center,
                fontSize = 48.sp,
                fontWeight = FontWeight.Bold,
                lineHeight = 48.sp
            )

            Text(
                modifier = Modifier
                    .padding(top = 16.dp, bottom = 16.dp)
                    .fillMaxWidth(),
                text = state.signInError ?: "",
                color = MaterialTheme.colorScheme.error,
                textAlign = TextAlign.Center
            )

            Text(
                modifier = Modifier.fillMaxWidth(),
                text = state.passwordResetMessage ?: "",
                color = MaterialTheme.colorScheme.tertiary,
                textAlign = TextAlign.Center
            )

            AnimatedVisibility(visible = state.loadingSignIn) {
                Column {
                    LinearProgressIndicator(
                        modifier = Modifier.fillMaxWidth(),
                        color = MaterialTheme.colorScheme.tertiary
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                }
            }

            SignInBlock(
                modifier = Modifier.fillMaxWidth(),
                onEmailChanged = { currentEmail = it }
            ) { email, password ->
                viewModel.onEvent(SignInEvents.OnSignInClick(email, password))
            }

            Text(
                modifier = Modifier
                    .padding(top = 16.dp, bottom = 16.dp)
                    .clickable {
                        viewModel.onEvent(SignInEvents.OnPasswordResetEmailChanged(currentEmail))
                        viewModel.onEvent(SignInEvents.OnSetSendPasswordResetDialog(true))
                    },
                text = "Forgot Password?",
                color = MaterialTheme.colorScheme.tertiary
            )
        }
        }

        if (state.initializingUser) {
            Box(
                modifier = Modifier
                    .background(Color.White.copy(alpha = 0.5f))
                    .fillMaxSize()
            ) {
                CircularProgressIndicator(
                    modifier = Modifier.align(Alignment.Center),
                    color = MaterialTheme.colorScheme.tertiary
                )
            }
        }
    }

    if(state.showSendPasswordResetDialog){
        Dialog(
            onDismissRequest = {
                viewModel.onEvent(SignInEvents.OnSetSendPasswordResetDialog(false))
            }
        ){
            Card(
                modifier = Modifier.responsiveDialogWidth(),
                shape = RoundedCornerShape(16.dp),
            ) {
                Column(
                    modifier = Modifier.padding(16.dp)
                ){
                    Text(
                        text = "Please enter your email address to reset your password",
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    TimesheetTextField(
                        text = state.passwordResetEmail,
                        placeholder = "Email",
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Email,
                            imeAction = ImeAction.Done
                        ),
                        keyboardActions = KeyboardActions(
                            onDone = { viewModel.onEvent(SignInEvents.OnSendPasswordResetEmail) }
                        ),
                        onTextChange = {
                            viewModel.onEvent(
                                SignInEvents.OnPasswordResetEmailChanged(it)
                            )
                        }
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    DialogButtonRow(
                        onConfirmClick = {
                            viewModel.onEvent(SignInEvents.OnSendPasswordResetEmail)
                        },
                        confirmButtonText = "Send reset email",
                        confirmLoading = state.loadingPasswordReset,
                        onDismissClick = {
                            viewModel.onEvent(
                                SignInEvents.OnSetSendPasswordResetDialog(false)
                            )
                        },
                        dismissButtonText = "Cancel"
                    )
                }
            }
        }
    }
}